import { Box, CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import toast from "react-hot-toast";
import styles from "./FileUploadField.module.css";

type FieldName =
  | "logo"
  | "eightyGCertificate"
  | "trustDeedCertificate"
  | "donationQr";

interface FileUploadFieldProps {
  name: string;
  control: Control<any>;
  errors: FieldErrors<any>;
  label: string;
  fieldabel: string;
  subText?: string;
  accept?: string;
  previewUrl?: string | null;
  onPreviewChange?: (url: string | null) => void;
  showFileName?: boolean;
  entityId?: string;
  fieldKey: FieldName;
  isEdit?: boolean;
  onUpload?: (id: string, formData: FormData) => Promise<any>;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  name,
  control,
  errors,
  label,
  fieldabel,
  subText,
  accept = "image/*,application/pdf",
  previewUrl: externalPreviewUrl,
  onPreviewChange,
  showFileName = true,
  entityId,
  fieldKey,
  isEdit = false,
  onUpload,
}) => {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (
    file: File,
    onChange: (value: any) => void
  ) => {
    if (!file) return;

    if (isEdit && entityId && onUpload) {
      // EDIT MODE: Upload immediately
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append(fieldKey, file);

        const response = await onUpload(entityId, formData);

        if (response?.success) {
          toast.success(response?.message || `${fieldabel} updated`);
        }

        const uploaded = response?.data?.[fieldKey] || response[fieldKey];
        if (uploaded?.url) {
          if (onPreviewChange) {
            onPreviewChange(uploaded.url);
          }
          // Update form value to URL string so parent doesn't try to re-upload file
          onChange(uploaded.url);
        } else {
          // Fallback if no URL returned, though unexpected in this flow
          onChange(file);
        }
      } catch (err) {
        console.error(err);
        toast.error(`Failed to upload ${fieldabel.toLowerCase()}`);
        onChange(file);
      } finally {
        setUploading(false);
      }
    } else {
      // CREATE
      onChange(file);

      if (file.type.startsWith("image/") && onPreviewChange) {
        onPreviewChange(URL.createObjectURL(file));
      } else if (onPreviewChange) {
        onPreviewChange(null);
      }
    }
  };

  return (
    <Box>
      <label className={styles.fieldLabel}>{fieldabel}</label>

      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => {
          const currentFile = value instanceof File ? value : null;
          const displayPreview =
            externalPreviewUrl ||
            (currentFile && currentFile.type.startsWith("image/")
              ? URL.createObjectURL(currentFile)
              : value && typeof value === "string"
              ? value
              : null);

          // Handle S3 URLs with query params for filename display
          const fileName =
            currentFile?.name ||
            (typeof externalPreviewUrl === "string"
              ? externalPreviewUrl.split("/").pop()?.split("?")[0]
              : "") ||
            "";

          // Regex to detect images (relaxed for query params)
          const isImagePreview =
            (displayPreview &&
              /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(displayPreview)) ||
            (currentFile && currentFile.type.startsWith("image/"));

          return (
            <div className={styles.uploadWrapper}>
              <input
                type="file"
                accept={accept}
                id={`${name}-upload`}
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileChange(file, onChange);
                  }
                }}
                disabled={uploading}
              />

              <label
                htmlFor={`${name}-upload`}
                className={styles.uploadLabel}
                style={{
                  opacity: uploading ? 0.6 : 1,
                  cursor: uploading ? "not-allowed" : "pointer",
                }}
              >
                <Box className={styles.uploadBox}>
                  {uploading ? (
                    <div className={styles.loadingState}>
                      <CircularProgress size={28} />
                      <p>Uploading...</p>
                    </div>
                  ) : displayPreview || currentFile ? (
                    <>
                      {isImagePreview ? (
                        <img
                          src={displayPreview!}
                          alt={fieldabel}
                          className={styles.previewImage}
                        />
                      ) : (
                        <p className={styles.fileName}>{fileName}</p>
                      )}
                      <p className={styles.changeText}>Click to replace</p>
                    </>
                  ) : (
                    <>
                      <span className={styles.uploadIcon}>{label}</span>
                      {subText && (
                        <p className={styles.uploadText}>{subText}</p>
                      )}
                    </>
                  )}
                </Box>
              </label>
            </div>
          );
        }}
      />

      {errors[name] && (
        <p className={styles.errorText}>{errors[name]?.message as string}</p>
      )}
    </Box>
  );
};

export default FileUploadField;
